// src/pages/Home.tsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

import Header from '../components/Header';
import CategoryList from '../components/CategoryList';
import BrandList from '../components/BrandList';
import ProductCard from '../components/ProductCard'; // Assuming ProductCard accepts new props
import { fetchCategories, fetchProducts, fetchBrands, fetchAllMtPrices, fetchCustomer } from '../utils/api'; // Import fetchCustomer
import { Category, Product, Brand, appConfig, Customer } from '../utils/types'; // Import Customer type
import Pagination from '../components/Pagination';
import { Tooltip as ReactTooltip } from "react-tooltip";
import { FaBars } from 'react-icons/fa';
import { checkSession } from '../utils/api';
import { hasRole, isAuthenticated } from '../utils/auth';
import { useConfig } from '../context/ConfigContext';
// --- Import Context Hooks ---
import { useAuth } from '../context/AuthContext';
import { useCustomerContext } from '../context/customerContext';

// Define the structure returned by fetchAllMtPrices (adjust if needed)
interface FetchedMTPrice {
    id: number; // Product ID
    price: number;
    // Add other fields if returned, like product_name
}

// Define the structure we'll use in state (Map for efficient lookup)
// The value includes the price and the role it applies to
type MTPriceMap = Map<number, { price: number; role: string }>;

const Home = () => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [errorProducts, setErrorProducts] = useState<string | null>(null);
    const [sort, setSort] = useState('updated_at DESC');
    const [perPage] = useState(60);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [isBrandListVisible, setIsBrandListVisible] = useState(false);
    const [isCategoryListVisible, setIsCategoryListVisible] = useState(false);
    const brandMenuRef = useRef<HTMLDivElement>(null);
    const categoryMenuRef = useRef<HTMLDivElement>(null);

    // --- State for MT Prices ---
    const [mtPrices, setMtPrices] = useState<MTPriceMap>(new Map());
    const [loadingPrices, setLoadingPrices] = useState(false);
    const [errorPrices, setErrorPrices] = useState<string | null>(null);

    // --- Get User and Customer Context ---
    const { user: loggedInUser } = useAuth();
    const { selectedSessionCustomer } = useCustomerContext();
    const { settings } = useConfig(); // Get app settings if needed

    // --- Determine Effective User (for pricing and actions) ---
    const effectiveUser = useMemo(() => {
        const isAdminOrStaff = hasRole(['super_admin', 'staff']);
        const isAdminActingAs = isAdminOrStaff && selectedSessionCustomer;

        if (isAdminActingAs) {
            console.log("Admin acting as customer:", selectedSessionCustomer);
            const customerRole = selectedSessionCustomer?.role || 'dealer';

            return {
                id: selectedSessionCustomer.user_id,
                role: customerRole,
                isAuthenticated: true,
                customer_id: selectedSessionCustomer.id
            };
        } else {
            let loggedInUserRole: string | undefined;
            let loggedInCustomerId: number | undefined;
            try {
                const rolesPermissionsItem = localStorage.getItem('rolesPermissions');
                if (rolesPermissionsItem) {
                    const rolesPermissions = JSON.parse(rolesPermissionsItem);
                    loggedInUserRole = rolesPermissions?.roles?.[0];
                }
                // --- Get customer_id from localStorage for logged-in user ---
                const storedCustomerId = localStorage.getItem('customer');
                if (storedCustomerId) {
                    // Assuming it's stored as just the ID number after JSON.parse
                    const parsedId = JSON.parse(storedCustomerId);
                    if (typeof parsedId === 'number') {
                        loggedInCustomerId = parsedId;
                    } else if (typeof parsedId === 'object' && parsedId !== null && typeof parsedId.id === 'number') {
                        // Handle case where it might be stored as { id: number }
                        loggedInCustomerId = parsedId.id;
                    }
                }
            } catch (e) {
                console.error("Failed to parse rolesPermissions or customer ID from localStorage in Home:", e);
            }

            return {
                id: loggedInUser?.id,
                role: loggedInUserRole,
                isAuthenticated: !!loggedInUser,
                customer_id: loggedInCustomerId // Use the ID retrieved from localStorage
            };
        }
    }, [loggedInUser, selectedSessionCustomer]);
    // --- End Determine Effective User ---

    // --- State to hold the full customer data for the effective user ---
    const [effectiveCustomerData, setEffectiveCustomerData] = useState<Customer | null>(null);
    const [loadingCustomerData, setLoadingCustomerData] = useState(false);
    const [errorCustomerData, setErrorCustomerData] = useState<string | null>(null);

    // --- Fetch Effective Customer Data ---
    useEffect(() => {
        const loadEffectiveCustomer = async () => {
            // Only fetch if we have an authenticated user with a customer_id
            if (effectiveUser.isAuthenticated && effectiveUser.customer_id) {
                setLoadingCustomerData(true);
                setErrorCustomerData(null);
                try {
                    // console.log(`Fetching customer data for ID: ${effectiveUser.customer_id}`); // Debug log
                    const customerData = await fetchCustomer(effectiveUser.customer_id);
                    setEffectiveCustomerData(customerData);
                    // console.log("Effective customer data loaded:", customerData); // Debug log
                } catch (err: any) {
                    console.error("Failed to load effective customer data:", err);
                    setErrorCustomerData(err.message || 'Failed to load customer details.');
                    setEffectiveCustomerData(null); // Clear on error
                } finally {
                    setLoadingCustomerData(false);
                }
            } else {
                // If not authenticated or no customer_id, clear the data
                setEffectiveCustomerData(null);
                setLoadingCustomerData(false);
                setErrorCustomerData(null);
                if (effectiveUser.isAuthenticated && !effectiveUser.customer_id) {
                    console.warn("Authenticated user has no associated customer_id."); // Debug log
                }
            }
        };

        loadEffectiveCustomer();
    }, [effectiveUser.isAuthenticated, effectiveUser.customer_id]); // Re-run if auth status or customer_id changes


    // --- Debounced Search Handler ---
    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            setSearch(value); // Update search state after debounce
            setCurrentPage(1); // Reset to first page on new search
        }, 300); // 300ms debounce time
    }, []);

    // --- Click Outside Handlers for Menus ---
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isBrandListVisible && brandMenuRef.current && !brandMenuRef.current.contains(event.target as Node)) {
                setIsBrandListVisible(false);
            }
            if (isCategoryListVisible && categoryMenuRef.current && !categoryMenuRef.current.contains(event.target as Node)) {
                setIsCategoryListVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isBrandListVisible, isCategoryListVisible]);

    // --- Fetch Initial Categories & Brands ---
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [brandData, categoryData] = await Promise.all([fetchBrands(), fetchCategories()]);
                setBrands(brandData);
                setCategories(categoryData);
            } catch (err) {
                console.error('Failed to load initial brands/categories:', err);
                // Optionally set an error state here
            }
        };
        loadInitialData();
    }, []); // Run only once on mount

    // --- Fetch Products ---
    useEffect(() => {
        const loadProducts = async () => {
            setLoadingProducts(true);
            setErrorProducts(null);
            setErrorPrices(null); // Clear price error when fetching new products
            setMtPrices(new Map()); // Clear old prices
            try {
                const { products: fetchedProducts, total_pages, total_products } = await fetchProducts(
                    currentPage,
                    search,
                    selectedCategory?.id || null,
                    selectedBrand?.id || null,
                    perPage,
                    sort
                );
                setProducts(fetchedProducts);
                setTotalPages(total_pages);
                setTotalProducts(total_products);
            } catch (err: any) {
                setErrorProducts(err.message || 'Failed to load products.');
                setProducts([]); // Clear products on error
            } finally {
                setLoadingProducts(false);
            }
        };
        loadProducts();
    }, [currentPage, search, selectedCategory, selectedBrand, perPage, sort]);

    // --- Fetch MT Prices (depends on products and effectiveUser) ---
    useEffect(() => {
        // Only fetch if products are loaded, user is authenticated, and has ID/Role
        if (products.length > 0 && effectiveUser.isAuthenticated && effectiveUser.id && effectiveUser.role) {
            const productIds = products.map(p => p.id);

            const loadPrices = async () => {
                setLoadingPrices(true);
                setErrorPrices(null);
                try {
                    // Call API with product IDs and effective user details
                    const priceDataArray: FetchedMTPrice[] = await fetchAllMtPrices(
                        productIds,
                        effectiveUser.id,
                        effectiveUser.role
                    );

                    // Convert array to Map for easy lookup in ProductCard
                    const priceMap: MTPriceMap = new Map();
                    priceDataArray.forEach(item => {
                        // Store price and the role it applies to
                        priceMap.set(item.id, { price: item.price, role: effectiveUser.role! });
                    });
                    setMtPrices(priceMap);

                } catch (err: any) {
                    console.error("Failed to load MT prices:", err);
                    setErrorPrices(err.message || 'Failed to load prices.');
                    setMtPrices(new Map()); // Clear prices on error
                } finally {
                    setLoadingPrices(false);
                }
            };
            loadPrices();
        } else {
            // Clear prices if no products or no valid user/role
            setMtPrices(new Map());
            setLoadingPrices(false); // Ensure loading is false
            setErrorPrices(null); // Ensure error is null
        }
    }, [products, effectiveUser]); // Re-run when products list or effective user changes

    // --- Session Check Interval (Keep as is) ---
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user && user.remember_token && user.session_id && !hasRole(['super_admin', 'staff'])) {
            const interval = setInterval(() => {
                checkSession();
            }, 5 * 60 * 1000); // Check every 5 minutes for non-admins
            return () => clearInterval(interval);
        }
    }, []);


    // --- Helper Functions ---
    const resetSelectedCategory = () => {
        setSelectedCategory(null);
        setCurrentPage(1); // Reset page
    };
    const resetSelectedBrand = () => {
        setSelectedBrand(null);
        setCurrentPage(1); // Reset page
    };

    // --- Render Logic ---
    return (
        <div>
            <Helmet>
                <title>{settings?.remark || 'Home'}</title>
                {/* Add other meta tags */}
            </Helmet>

            <Header />

            {/* Search, Sort, Filters */}
            <div className="sticky top-10 z-20 bg-white flex flex-col md:flex-row items-center mt-1 mb-4 p-2 border-b">
                {/* Search Input */}
                <div className='text-md w-full md:w-3/5 lg:w-4/6 mb-2 md:mb-0 md:pr-2'>
                    <form onSubmit={(e) => e.preventDefault()}>
                        <input
                            type="search"
                            name="search"
                            placeholder="Search products..."
                            onChange={handleSearchChange}
                            className="search-cancel:appearance-none bg-blue-50 border border-blue-300 px-2 py-1 w-full rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                    </form>
                </div>

                {/* Sort Options */}
                <div className='text-xs md:text-sm w-full md:w-2/5 lg:w-2/6 flex justify-end'>
                    <select
                        name="sort"
                        onChange={(e) => { setSort(e.target.value); setCurrentPage(1); }}
                        value={sort}
                        className='w-full h-8 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500'
                    >
                        <option value="updated_at DESC">Sort: Newest</option>
                        <option value="updated_at ASC">Sort: Oldest</option>
                        <option value="product_value ASC">Sort: Price Low-High</option>
                        <option value="product_value DESC">Sort: Price High-Low</option>
                        <option value="product_name ASC">Sort: Name A-Z</option>
                        <option value="product_name DESC">Sort: Name Z-A</option>
                    </select>
                </div>
            </div>

            {/* Category/Brand Toggles & Menus */}
            <div className="relative mb-4 px-2"> {/* Container for buttons */}
                <button
                    className="relative inline-flex items-center mr-2 p-2 bg-orange-100 text-xs text-orange-700 rounded hover:bg-orange-200 focus:outline-none focus:ring-1 focus:ring-orange-400"
                    onClick={() => setIsCategoryListVisible(!isCategoryListVisible)}
                >
                    <FaBars size={14} className="mr-1" />
                    <span>Category</span>
                </button>
                <button
                    className="relative inline-flex items-center p-2 bg-purple-100 text-xs text-purple-700 rounded hover:bg-purple-200 focus:outline-none focus:ring-1 focus:ring-purple-400"
                    onClick={() => setIsBrandListVisible(!isBrandListVisible)}
                >
                    <FaBars size={14} className="mr-1" />
                    <span>Brand</span>
                </button>

                {/* Category Menu */}
                <div ref={categoryMenuRef} className={`absolute top-full left-0 mt-1 z-30 w-full max-w-xs sm:max-w-sm md:max-w-md bg-orange-50 border border-orange-200 rounded shadow-lg category-menu ${isCategoryListVisible ? 'block' : 'hidden'}`}>
                    {isCategoryListVisible && <CategoryList categories={categories} setSelectedCategory={(cat) => { setSelectedCategory(cat); setCurrentPage(1); setIsCategoryListVisible(false); }} selectedCategory={selectedCategory} />}
                </div>

                {/* Brand Menu - Position relative to its button or main container */}
                <div ref={brandMenuRef} className={`absolute top-full left-0 mt-1 z-30 w-full max-w-xs sm:max-w-sm md:max-w-md bg-purple-50 border border-purple-200 rounded shadow-lg brand-menu ${isBrandListVisible ? 'block' : 'hidden'}`}>
                    {isBrandListVisible && <BrandList brands={brands} setSelectedBrand={(brand) => { setSelectedBrand(brand); setCurrentPage(1); setIsBrandListVisible(false); }} selectedBrand={selectedBrand} />}
                </div>
            </div>


            {/* Main Content Area */}
            <section className="products-section px-2">

                {/* Selected Filters Display & Reset */}
                <div className='mb-4 flex flex-wrap items-center gap-2'>
                    <span className='text-sm font-medium'>Filters:</span>
                    <button className={`inline-flex items-center px-2 py-1 text-xs rounded-lg shadow ${selectedCategory ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-500'}`}>
                        Category: <span className='font-semibold ml-1'>{selectedCategory ? selectedCategory.cat_name : 'All'}</span>
                        {selectedCategory && (
                            <span onClick={resetSelectedCategory} className="ml-2 text-red-500 cursor-pointer hover:text-red-700" data-tooltip-id='resetCategoryTooltip' data-tooltip-content='Reset Category Filter'>
                                &times; {/* Use times symbol */}
                            </span>
                        )}
                    </button>
                    <ReactTooltip id='resetCategoryTooltip' place="bottom" style={{ backgroundColor: "rgb(254 243 199)", color: "rgb(180 83 9)", fontSize: '0.75rem', zIndex: 50 }} />

                    <button className={`inline-flex items-center px-2 py-1 text-xs rounded-lg shadow ${selectedBrand ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-500'}`}>
                        Brand: <span className='font-semibold ml-1'>{selectedBrand ? selectedBrand.cat_name : 'All'}</span>
                        {selectedBrand && (
                            <span onClick={resetSelectedBrand} className="ml-2 text-red-500 cursor-pointer hover:text-red-700" data-tooltip-id='resetBrandTooltip' data-tooltip-content='Reset Brand Filter'>
                                &times;
                            </span>
                        )}
                    </button>
                    <ReactTooltip id='resetBrandTooltip' place="bottom" style={{ backgroundColor: "rgb(254 243 199)", color: "rgb(180 83 9)", fontSize: '0.75rem', zIndex: 50 }} />

                    {/* Total Products Count */}
                    {!loadingProducts && (
                        <span className='ml-auto text-xs md:text-sm font-medium text-gray-600'>
                            {totalProducts} Products Found
                        </span>
                    )}
                </div>

                {/* Loading/Error States */}
                {loadingProducts && (
                    <div className="text-center py-10"> {/* Changed p to div */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            {Array.from({ length: 8 }).map((_, n) => (
                                <div className="animate-pulse space-y-4" key={n}>
                                    <div className="h-48 bg-gray-200 rounded-xl"></div>
                                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                                </div>
                            ))}
                        </div>
                        <p className="mt-4">Loading products...</p> {/* Wrapped text in its own p tag */}
                    </div>

                )}
                {errorProducts && <p className="text-center py-10 text-red-500">{errorProducts}</p>}
                {/* Optional: Show price loading/error separately */}
                {loadingPrices && !loadingProducts && <p className="text-center text-sm text-gray-500">Loading prices...</p>}
                {errorPrices && !loadingProducts && <p className="text-center text-sm text-red-500">{errorPrices}</p>}
                {/* Loading/Error States for Customer Data */}
                {loadingCustomerData && <p className="text-center text-sm text-gray-500">Loading customer details...</p>}
                {errorCustomerData && <p className="text-center text-sm text-red-500">{errorCustomerData}</p>}


                {/* Product Grid */}
                <div className="flex flex-wrap -mx-1">
                    {!loadingProducts && products.length > 0 ? (
                        products.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                mtPriceData={mtPrices.get(product.id)}
                                effectiveUserId={effectiveUser.id}
                                effectiveUserRole={effectiveUser.role}
                                effectiveUserIsAuthenticated={effectiveUser.isAuthenticated}
                                // --- Pass the fetched customer data ---
                                customerForAction={effectiveCustomerData}
                                // --- Pass loading state ---
                                isLoadingCustomerForAction={loadingCustomerData}
                                // --- Add this prop ---
                                loadingPrices={loadingPrices}
                                // --- End add prop ---
                            />
                        ))
                    ) : (
                        // Show message only if not loading and filters/search might be active
                        !loadingProducts && (selectedCategory || selectedBrand || search) && (
                            <div className='w-full text-center py-10'>
                                <p className='text-gray-600'>No products found matching your criteria.</p>
                            </div>
                        )
                    )}
                    {/* Show message if not loading and no products at all */}
                    {!loadingProducts && products.length === 0 && !search && !selectedCategory && !selectedBrand && (
                        <div className='w-full text-center py-10'>
                            <p className='text-gray-600'>No products available.</p>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {!loadingProducts && totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                )}
            </section>
        </div>
    );
};

export default Home;
